import React, { useEffect, useState } from 'react';
import { Banner, Button, Dialog, Spinner } from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import { AudioPlayer, apiError } from '../../next/ui';

/**
 * Preview of one Yemot file: audio plays, text (ini, tts, txt, ymgr…) is shown as is, images and PDFs render,
 * anything else offers a download. /ivr/file/preview decides the kind; /ivr/file/raw serves the bytes.
 */
const RAW = '/ivr/file/raw';

async function fetchBlob(path, download) {
  const { data } = await api.get(RAW, { params: { path, ...(download ? { download: 1 } : {}) }, responseType: 'blob' });
  return data;
}

function BlobView({ path, kind, name }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    fetchBlob(path)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setError('לא ניתן לטעון את הקובץ'); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);
  if (error) return <Banner tone="danger" icon="alert">{error}</Banner>;
  if (!url) return <div className="ck-empty"><Spinner size={24} /></div>;
  if (kind === 'image') return <img src={url} alt={name} style={{ maxWidth: '100%', maxHeight: '60vh', display: 'block', margin: '0 auto' }} />;
  return <iframe src={url} title={name} style={{ width: '100%', height: '60vh', border: 0 }} />;
}

function Preview({ file, onClose }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const { path } = file;

  useEffect(() => {
    let cancelled = false;
    api.get('/ivr/file/preview', { params: { path } })
      .then(({ data }) => { if (!cancelled) setPreview(data); })
      .catch((e) => { if (!cancelled) setError(apiError(e, 'שגיאה בטעינת הקובץ')); });
    return () => { cancelled = true; };
  }, [path]);

  const download = async () => {
    setDownloading(true);
    try {
      const url = URL.createObjectURL(await fetchBlob(path, true));
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(apiError(e, 'שגיאה בהורדת הקובץ'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={file.name}
      icon={preview?.kind === 'audio' ? 'mic' : 'file'}
      size="lg"
      footer={(
        <>
          <Button icon="download" disabled={downloading} onClick={download}>{downloading ? 'מוריד…' : 'הורדה'}</Button>
          <span className="ck-grow" />
          <Button variant="ghost" onClick={onClose}>סגירה</Button>
        </>
      )}
    >
      <div className="nx-stack">
        <small className="ck-muted" dir="ltr" style={{ textAlign: 'start' }}>{file.displayPath}</small>
        {error ? <Banner tone="danger" icon="alert">{error}</Banner> : null}
        {!preview && !error ? <div className="ck-empty"><Spinner size={24} /></div> : null}
        {preview?.kind === 'audio' ? <AudioPlayer src={`${RAW}?path=${encodeURIComponent(path)}`} label={file.name} compact={false} /> : null}
        {preview?.kind === 'image' || preview?.kind === 'pdf' ? <BlobView path={path} kind={preview.kind} name={file.name} /> : null}
        {preview?.kind === 'text' ? (
          <>
            {preview.truncated ? <Banner tone="info" icon="info">הקובץ ארוך ומוצגת רק תחילתו. הקובץ המלא זמין בהורדה.</Banner> : null}
            {preview.text ? (
              <pre dir="auto" style={{ margin: 0, padding: 12, maxHeight: '60vh', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 13, background: 'var(--ck-surface-2, rgba(127,127,127,.08))', borderRadius: 8, unicodeBidi: 'plaintext' }}>
                {preview.text}
              </pre>
            ) : <span className="ck-muted">הקובץ ריק</span>}
          </>
        ) : null}
        {preview?.kind === 'binary' ? <Banner tone="info" icon="info">אי אפשר להציג את סוג הקובץ הזה בדפדפן. אפשר להוריד אותו ולפתוח במחשב.</Banner> : null}
      </div>
    </Dialog>
  );
}

/** the content lives only while a file is open, so every opening starts clean */
export default function IvrFilePreviewDialog({ file, onClose }) {
  return file ? <Preview key={file.path} file={file} onClose={onClose} /> : null;
}
